import React, { useEffect, useState } from 'react';
import { useFeathers, isPermitted } from '../../app/util';
import { useSelector } from 'react-redux';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { DataDetailToolbar, LoadingSpinner, PaginationControl, PaginationStatus, StepIndicator } from '../../components';
import { CandidateList } from '../../app/feathers/candidate-lists/CandidateList';
import moment from 'moment';
import { Add as AddIcon } from '@mui/icons-material';
import { Application } from '@feathersjs/feathers';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';

const Wrapper = styled.div`
  width: calc(100% - 2 * 24px);
  display: flex;
  flex-direction: column;
  padding: 24px;
  align-items: stretch;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  margin-bottom: 16px;

  h1 {
    font-size: 28px;
    margin: 0;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  border-radius: 8px;
  border: 1px #EEEEEE solid;
  overflow: hidden;
  background: #FFFFFF;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 380px 1fr 160px;
  grid-gap: 24px;
  padding: 16px 24px;
  background: #F7F9FE;
  border-bottom: 1px solid #EEEEEE;
  align-items: center;

  h5 {
    margin: 0;
    font-weight: bold;
    color: #313639;
  }
`;

const TableRow = styled.div<{ onClick?: () => void }>`
  display: grid;
  grid-template-columns: 2fr 380px 1fr 160px;
  grid-gap: 24px;
  padding: 16px 24px;
  border-bottom: 1px solid #EEEEEE;
  align-items: center;
  cursor: ${props => props.onClick ? 'pointer' : 'default'};
  transition: background-color 0.2s;

  &:hover {
    background-color: ${props => props.onClick ? '#F7F9FE' : 'transparent'};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const LoadingTile = styled.div`
  background-color: #FFFFFF;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px;

  span {
    margin-bottom: 20px;
  }
`;

const CandidateLists = () => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const { key: electionKey } = useParams<{ key: string }>();
  const history = useHistory();
  const location = useLocation();
  const user = useSelector((state: any) => state.user);
  const electionData = useSelector((state: any) => state?.elections?.byKey[electionKey]);
  
  const [candidateLists, setCandidateLists] = useState<CandidateList[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [cancellingCandidateListIds, setCancellingCandidateListIds] = useState<Set<string>>(new Set());
  const itemsPerPage = 10;

  const candidateListSteps = [
    { name: 'Uploaded' },
    { name: 'Grouped' },
    { name: 'Labeled' },
    { name: 'Matched' },
    { name: 'Scouted' },
    { name: 'Created' },
  ];

  const canCreateCandidateLists = isPermitted(user, ['editor', 'admin', 'super-admin']);

  const getCandidateLists = async (page: number = 0) => {
    setLoading(true);
    try {
      const response = await feathers.getService('candidate-lists').find({
        query: {
          election: electionKey,
          $skip: page * itemsPerPage,
          $limit: itemsPerPage,
          $sort: { createdAt: -1 }
        }
      });
      setCandidateLists(response.data || []);
      setTotalCount(response.total || 0);
    } catch (error) {
      console.error('Error fetching candidate lists:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (feathers && electionKey) {
      getCandidateLists(currentPage);
    }
  }, [feathers, electionKey, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page - 1);
  };

  const handleRowClick = (candidateList: CandidateList) => {
    history.push(`/elections/${electionKey}/candidate-lists/${candidateList._id}`);
  };

  const handleCreateNew = () => {
    history.push(`/elections/${electionKey}/candidate-lists/new`);
  };

  const handleCancelCreateEntities = async (candidateList: CandidateList) => {
    setCancellingCandidateListIds(prev => new Set(prev).add(candidateList._id));
    try {
      const response = await feathers.getService('candidate-lists').patch(candidateList._id, {
        $cancelCreateEntities: true
      });

      setCandidateLists(prev => prev.map(list => (
        list._id === response._id ? response : list
      )));
    } catch (error) {
      console.error('Error cancelling candidate list creation:', error);
    } finally {
      setCancellingCandidateListIds(prev => {
        const next = new Set(prev);
        next.delete(candidateList._id);
        return next;
      });
    }
  };

  const getStepIndexFromStatus = (status?: string | null): number | null => {
    switch (status) {
      case 'started':
        return 0;
      case 'grouped':
        return 1;
      case 'labeled':
        return 2;
      case 'matched':
        return 3;
      case 'scouted':
        return 4;
      case 'created':
        return 5;
      default:
        return null;
    }
  };

  if (!electionData) {
    return (
      <Wrapper>
        <LoadingTile>
          <span>Loading election data...</span>
          <LoadingSpinner width={64} />
        </LoadingTile>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <DataDetailToolbar
        onBack={{ to: { pathname: `/elections/${electionKey}` }}}
        navTree={[
          {
            text: 'Elections',
            to: { pathname: `/elections` }
          },
          {
            text: electionData?.name || 'Election',
            to: { pathname: `/elections/${electionKey}` }
          },
          { text: 'Candidate Lists' }
        ]}
        actionButtonsComponent={
          canCreateCandidateLists ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
              style={{ backgroundColor: '#22034F', color: '#FFFFFF' }}
            >
              New Upload
            </Button>
          ) : null
        }
      />
      <Content>
        <TableHeader>
          <Typography variant="h5">Source</Typography>
          <Typography variant="h5">Stage</Typography>
          <Typography variant="h5">Updated</Typography>
          <Typography variant="h5">Actions</Typography>
        </TableHeader>

        {loading ? (
          <LoadingTile>
            <span>Loading candidate lists...</span>
            <LoadingSpinner width={64} />
          </LoadingTile>
        ) : candidateLists.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary">
              No candidate lists found for this election.
            </Typography>
            {canCreateCandidateLists && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleCreateNew}
                style={{ marginTop: '16px' }}
              >
                Upload First List
              </Button>
            )}
          </div>
        ) : (
          candidateLists.map((candidateList) => (
            <TableRow
              key={candidateList._id}
              onClick={() => handleRowClick(candidateList)}
            >
              <div>
                <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                  {candidateList.source}
                </Typography>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                {getStepIndexFromStatus(candidateList.status) === null ? (
                  <Typography variant="body2" color="textSecondary">
                    --
                  </Typography>
                ) : (
                  <StepIndicator
                    steps={candidateListSteps}
                    activeStepIndex={getStepIndexFromStatus(candidateList.status)!}
                  />
                )}
              </div>

              <Typography variant="body2">
                {moment(candidateList.updatedAt).format('MMM D, YYYY')}
              </Typography>
              
              <div style={{ display: 'flex', gap: '8px' }}>
                {canCreateCandidateLists && candidateList.processInProgress?.stage === 'creating' && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    disabled={cancellingCandidateListIds.has(candidateList._id)}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancelCreateEntities(candidateList);
                    }}
                  >
                    {cancellingCandidateListIds.has(candidateList._id) ? 'Cancelling...' : 'Cancel'}
                  </Button>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRowClick(candidateList);
                  }}
                >
                  View
                </Button>
              </div>
            </TableRow>
          ))
        )}
      </Content>

      {totalCount > itemsPerPage && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginTop: '24px' 
        }}>
          <PaginationStatus
            activePage={currentPage + 1}
            items={totalCount}
            itemsPerPage={itemsPerPage}
            itemName="candidate list"
          />
          <PaginationControl
            activePage={currentPage + 1}
            items={totalCount}
            itemsPerPage={itemsPerPage}
            onChange={handlePageChange}
          />
        </div>
      )}
    </Wrapper>
  );
};

export default CandidateLists; 